/* eslint-disable */
import { useMemo, useState } from 'react';
import { Button, Col } from 'react-bootstrap';
import _ from 'lodash';
import PropTypes from 'prop-types';
import * as ReactTable from '@tanstack/react-table';

const { useReactTable, getCoreRowModel, getPaginationRowModel, flexRender } = ReactTable;
import ArrowButton from '../../../components/common/button-arrow';

import './datatable.css';
import './user-frontend-datatable.css';

// TanStack column `size` feeds <th style={{ width }}>. React only appends `px`
// to NUMBER values, so a numeric string like actionWidth="400" renders an
// invalid `width:400` and the column collapses. Coerce numeric strings to
// numbers; leave non-numeric values (e.g. "50%") untouched.
const toSize = (v) => {
  if (v == null) return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? v : n;
};

// Replicates the old wrapper's renderNested(cell, row, attr). `cell` is the row
// when hasAttributes=false; the row.attributes object when hasAttributes=true.
const renderNested = (cell, row, attr, hasAttributes) => {
  if (typeof attr.render === 'function' && !attr.render(cell, row)) {
    return null;
  }

  let data = hasAttributes ? _.get(cell, attr.prop) : _.get(row, attr.prop);
  if (typeof attr.format === 'function') {
    data = attr.format(data, row);
  }
  // No manual HTML-escaping: `data` is rendered as a React text child below, and React
  // already escapes text for the DOM. The previous wrapper rendered cells via innerHTML
  // so it needed escaping; doing it here instead double-encoded, so "02/06/2026" showed
  // literally as "02&#47;06&#47;2026" (and commas/parens likewise) — IGN-2365.

  if (typeof attr.onClick === 'function') {
    return (
      <Button variant="link" onClick={() => attr.onClick(data, row)}>
        {data}
      </Button>
    );
  }

  return data;
};

const renderActionsCell = (row, actions, inlineActions) => {
  const actionsCount = inlineActions ? actions.length : 1;
  return (
    <div className="actions-column row">
      {actions.map((item, index) => {
        if (typeof item === 'function') {
          return (
            <Col lg={12 / actionsCount} key={index}>
              {item(index, row)}
            </Col>
          );
        }
        if (typeof item.render === 'function' && !item.render(null, row)) {
          return <Col lg={12 / actionsCount} key={index}/>;
        }
        return (
          <Col lg={12 / actionsCount} key={index}>
            <ArrowButton
              permissions={item.permissions}
              arrow={false}
              submit={false}
              block={!inlineActions}
              color={item.color || 'red'}
              className={item.className}
              disabled={typeof item.disabled === 'function' ? item.disabled(row) : false}
              onClick={typeof item.callback === 'function' ? () => item.callback(row) : undefined}
            >
              {item.label}
            </ArrowButton>
          </Col>
        );
      })}
    </div>
  );
};

// Pagination control. Styling lives in datatable.scss under `.ignite-table-pagination`
// (the table renders inside `.ignite-table`, not the old `.react-bs-table-container`).
const PaginationBar = ({ currentPage, totalPages, sizePerPage, totalSize, onChange, paginationShowsTotal }) => {
  if (totalPages <= 0) return null;

  // Build a window of up to 5 page buttons around the current page.
  const windowSize = 5;
  let start = Math.max(1, currentPage - Math.floor(windowSize / 2));
  let end = start + windowSize - 1;
  if (end > totalPages) {
    end = totalPages;
    start = Math.max(1, end - windowSize + 1);
  }
  const pages = [];
  for (let p = start; p <= end; p += 1) pages.push(p);

  const goTo = (p) => {
    const target = Math.min(Math.max(1, p), totalPages);
    if (target !== currentPage) onChange(target);
  };

  const rangeStart = (currentPage - 1) * sizePerPage + 1;
  const rangeEnd = Math.min(currentPage * sizePerPage, totalSize);

  return (
    <div className="ignite-table-pagination">
      {typeof paginationShowsTotal === 'function' && totalSize > 0 && (
        <div className="ignite-table-pagination-total">
          {paginationShowsTotal(rangeStart, rangeEnd, totalSize)}
        </div>
      )}
      <ul className="react-bootstrap-table-page-btns-ul pagination">
        <li className={`page-item${currentPage <= 1 ? ' disabled' : ''}`}>
          <a className="page-link" href="#" aria-label="First page" onClick={(e) => { e.preventDefault(); goTo(1); }}>«</a>
        </li>
        <li className={`page-item${currentPage <= 1 ? ' disabled' : ''}`}>
          <a className="page-link" href="#" aria-label="Previous page" onClick={(e) => { e.preventDefault(); goTo(currentPage - 1); }}>‹</a>
        </li>
        {pages.map((p) => (
          <li key={p} className={`page-item${p === currentPage ? ' active' : ''}`}>
            <a className="page-link" href="#" onClick={(e) => { e.preventDefault(); goTo(p); }}>{p}</a>
          </li>
        ))}
        <li className={`page-item${currentPage >= totalPages ? ' disabled' : ''}`}>
          <a className="page-link" href="#" aria-label="Next page" onClick={(e) => { e.preventDefault(); goTo(currentPage + 1); }}>›</a>
        </li>
        <li className={`page-item${currentPage >= totalPages ? ' disabled' : ''}`}>
          <a className="page-link" href="#" aria-label="Last page" onClick={(e) => { e.preventDefault(); goTo(totalPages); }}>»</a>
        </li>
      </ul>
    </div>
  );
};

const IgniteDataTable = ({
  data,
  columns,
  actions,
  actionWidth,
  inlineActions = true,
  hideId = true,
  hasAttributes = true,
  pagination = false,
  remote = false,
  dataSize,
  tableClass,
  tableClassHeader,
  tableClassRow,
}) => {
  const paginationOpts = typeof pagination === 'object' && pagination !== null ? pagination : {};
  const controlledPage = paginationOpts.page;
  const sizePerPage = paginationOpts.sizePerPage || 10;
  const totalSize = dataSize != null ? dataSize : (Array.isArray(data) ? data.length : 0);
  const totalPages = pagination ? Math.max(1, Math.ceil(totalSize / sizePerPage)) : 1;

  // For uncontrolled local pagination, track page in local state. All current
  // consumers pass `page` so this branch is rare but kept for safety.
  const [localPage, setLocalPage] = useState(1);
  const currentPage = controlledPage || localPage;

  const onPageChange = (newPage) => {
    if (typeof paginationOpts.onPageChange === 'function') {
      paginationOpts.onPageChange(newPage, sizePerPage);
    }
    if (controlledPage == null) setLocalPage(newPage);
  };

  // Build TanStack columns. Each maps to one column on the old wrapper.
  const tanColumns = useMemo(() => {
    const cols = [];

    if (!hideId) {
      cols.push({
        id: 'id',
        accessorFn: (row) => row.id,
        header: 'ID',
        size: 100,
      });
    }

    columns.forEach((col, idx) => {
      cols.push({
        id: `${col.prop}-${idx}`,
        accessorFn: (row) => (hasAttributes ? _.get(row.attributes, col.prop) : _.get(row, col.prop)),
        header: typeof col.headOnClick === 'function'
          ? () => (
            <a
              href="#"
              className="text-ignite"
              onClick={(e) => {
                e.preventDefault();
                return col.headOnClick(col.prop);
              }}
            >
              {col.title}
            </a>
          )
          : col.title,
        cell: ({ row }) => {
          const cell = hasAttributes ? row.original.attributes : row.original;
          return renderNested(cell, row.original, col, hasAttributes);
        },
        size: toSize(col.width),
      });
    });

    if (actions && actions.length) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        size: toSize(actionWidth),
        cell: ({ row }) => renderActionsCell(row.original, actions, inlineActions),
      });
    }

    return cols;
  }, [columns, actions, hideId, hasAttributes, actionWidth, inlineActions]);

  // Pagination state for TanStack. When remote, table doesn't slice; parent
  // already gave us the current page's slice. Otherwise let TanStack slice.
  const table = useReactTable({
    data,
    columns: tanColumns,
    getCoreRowModel: getCoreRowModel(),
    ...(pagination && !remote
      ? {
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: false,
        state: { pagination: { pageIndex: currentPage - 1, pageSize: sizePerPage } },
        onPaginationChange: () => {},
        pageCount: totalPages,
      }
      : {}),
    ...(pagination && remote
      ? {
        manualPagination: true,
        pageCount: totalPages,
        state: { pagination: { pageIndex: currentPage - 1, pageSize: sizePerPage } },
        onPaginationChange: () => {},
      }
      : {}),
  });

  return (
    <div className={tableClass || 'ignite-table'}>
      <table className="table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={tableClassHeader || 'ignite-table-header'}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={header.column.columnDef.size ? { width: header.column.columnDef.size } : undefined}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={tableClassRow || 'ignite-table-row'}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pagination && (
        <PaginationBar
          currentPage={currentPage}
          totalPages={totalPages}
          sizePerPage={sizePerPage}
          totalSize={totalSize}
          onChange={onPageChange}
          paginationShowsTotal={paginationOpts.paginationShowsTotal}
        />
      )}
    </div>
  );
};

IgniteDataTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    prop: PropTypes.string.isRequired,
    title: PropTypes.string,
    onClick: PropTypes.func,
    render: PropTypes.func,
  })).isRequired,
  actions: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.oneOf(['red', 'yellow', 'grey', 'white']),
      className: PropTypes.any,
      permissions: PropTypes.shape({
        action: PropTypes.string,
        subjectClass: PropTypes.string,
        mode: PropTypes.oneOf(['hide', 'disable']),
      }),
      callback: PropTypes.func,
      render: PropTypes.func,
    }),
  ])),
  actionWidth: PropTypes.string,
  inlineActions: PropTypes.bool,
  hideId: PropTypes.bool,
  hasAttributes: PropTypes.bool,
  pagination: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  remote: PropTypes.bool,
  dataSize: PropTypes.number,
};

export default IgniteDataTable;
